import type { ReactNode } from 'react';
import { Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import type { AgentSlug, AgentStats } from '@diggens/types';
import { agentStyles } from '../../theme/theme';
import { formatWhen } from '@diggens/chat';

/** The agents every workspace has, and what their tools do; specialists describe themselves in the roster. */
export const CORE_AGENTS: Array<{ agent: AgentSlug; description: string; writes: boolean; chat?: { label: string; path: string } }> = [
  {
    agent: 'manager',
    description:
      'Your lead agent, in chat and Slack. It answers questions, sets up funds and portfolio companies from documents you upload, and plans work for your specialists; a plan step that changes data waits for an admin.',
    writes: true,
    chat: { label: 'Chat with your Chief of Staff', path: '/dashboard' },
  },
  {
    agent: 'data_analyst',
    description: 'Reads the documents portfolio companies send by email or upload and saves the metrics it extracts, noting the page each number came from.',
    writes: true,
  },
  { agent: 'analytics', description: 'Answers metric questions with trends, comparisons and charts, and saves dashboards, for the Chief of Staff and the Operations Manager.', writes: true },
];

/** Whether the team's stats have loaded: until they have, a card claims no status or record. */
export type StatsState = 'ready' | 'loading' | 'error';

function StatusChip({ stats, state }: { stats: AgentStats | undefined; state: StatsState }) {
  if (state !== 'ready') return null;
  if (stats && stats.waiting > 0) return <Chip label="Waiting for approval" size="small" color="warning" variant="outlined" />;
  if (stats && stats.working > 0) {
    return (
      <Chip
        label={stats.working > 1 ? `Working on ${stats.working}` : 'Working'}
        size="small"
        color="primary"
        variant="outlined"
        icon={<Box component="span" sx={{ width: 8, height: 8, ml: 1, borderRadius: '50%', backgroundColor: agentStyles.statusColors.processing, ...agentStyles.agentPulse }} />}
      />
    );
  }
  return <Chip label="Idle" size="small" variant="outlined" />;
}

interface TeamCardProps {
  name: string;
  description: string;
  writes: boolean;
  /** Its writes are record changes a user could make from the screens, so its plan steps run without approval. */
  approvalFree?: boolean;
  stats: AgentStats | undefined;
  statsState: StatsState;
  onOpenRun: (runId: string) => void;
  actions: ReactNode;
  /** A specialist the workspace has not recruited: a dashed card marked as not on the team, still showing its week. */
  available?: boolean;
  /** Chips after the name, such as a persona's source and version. */
  badges?: ReactNode;
}

const RECORD_UNKNOWN: Record<Exclude<StatsState, 'ready'>, string> = { loading: 'Loading its recent work…', error: 'Its recent work could not be loaded' };

/** One agent: what it does, whether it is working, its week, and the actions the page offers for it. */
export function TeamCard({ name, description, writes, approvalFree = false, stats, statsState, onOpenRun, actions, available = false, badges }: TeamCardProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...(available ? { borderStyle: 'dashed' } : {}) }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          {writes && (approvalFree ? <Chip label="Changes records, no approval" size="small" variant="outlined" /> : <Chip label="Changes data" size="small" color="warning" variant="outlined" />)}
          {badges}
        </Stack>
        {available ? <Chip label="Not on your team" size="small" variant="outlined" /> : <StatusChip stats={stats} state={statsState} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {description}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {statsState !== 'ready'
          ? RECORD_UNKNOWN[statsState]
          : stats
            ? `${stats.runs} run${stats.runs === 1 ? '' : 's'} in the last 7 days${stats.failed > 0 ? ` · ${stats.failed} failed` : ''}`
            : 'No runs in the last 7 days'}
      </Typography>
      {/* A delegated run opens the top-level run it belongs to */}
      {stats?.lastRun && (
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          Last, {formatWhen(stats.lastRun.startedAt)}:{' '}
          <Link component="button" variant="caption" onClick={() => onOpenRun(stats.lastRun.rootRunId)} sx={{ textAlign: 'left', verticalAlign: 'baseline' }}>
            {stats.lastRun.title}
          </Link>
        </Typography>
      )}
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {actions}
      </Stack>
    </Paper>
  );
}

/** One, two or three cards per row, for a team of any size. */
export function TeamGrid({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>{children}</Box>;
}
